import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/useAuth';
import { deleteProperty, setPropertyActive, subscribeToProperties, type Property } from '@/services/properties';

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToProperties((items) => {
      setProperties(items);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleDelete = async (property: Property) => {
    if (!confirm(`Excluir o imóvel "${property.title}"? Esta ação não pode ser desfeita.`)) return;
    setDeletingId(property.id);
    try {
      await deleteProperty(property.id);
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (property: Property) => {
    setTogglingId(property.id);
    try {
      await setPropertyActive(property.id, !property.active);
    } finally {
      setTogglingId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <main className="min-h-screen bg-background-100">
      <header className="bg-secondary-900 px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/navbar-logo-v2.png" alt="BNI" className="h-8 brightness-0 invert" />
          <span className="text-background-50 font-semibold text-sm hidden sm:inline">Painel Administrativo</span>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 text-xs font-semibold text-background-300 hover:text-background-50 transition-colors"
        >
          <i className="ri-logout-box-line"></i>
          Sair
        </button>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-secondary-900">Imóveis cadastrados</h1>
            <p className="text-sm text-foreground-600 mt-0.5">{properties.length} imóveis no site</p>
          </div>
          <button
            onClick={() => navigate('/admin/imoveis/novo')}
            className="flex items-center gap-2 bg-primary-500 text-background-50 px-4 py-2.5 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Novo Imóvel
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20 text-foreground-500">
            <i className="ri-loader-4-line text-2xl animate-spin"></i>
          </div>
        ) : properties.length === 0 ? (
          <div className="bg-background-50 rounded-lg border border-background-200/70 text-center py-16">
            <span className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-background-400">
              <i className="ri-home-smile-line text-2xl"></i>
            </span>
            <h3 className="text-lg font-semibold text-foreground-700 mb-1">Nenhum imóvel cadastrado ainda</h3>
            <p className="text-sm text-foreground-500 mb-5">Clique em "Novo Imóvel" para adicionar o primeiro.</p>
          </div>
        ) : (
          <div className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
            <div className="divide-y divide-background-200/70">
              {properties.map((property) => (
                <div key={property.id} className={`flex items-center gap-4 p-4 ${property.active ? '' : 'opacity-60'}`}>
                  <div className="w-20 h-16 rounded-md overflow-hidden bg-background-200 flex-shrink-0">
                    {property.image ? (
                      <img src={property.image} alt={property.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-background-400">
                        <i className="ri-image-line"></i>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider text-background-50 ${
                          property.tag === 'VENDA' ? 'bg-primary-500' : 'bg-secondary-500'
                        }`}
                      >
                        {property.tag}
                      </span>
                      <span className="text-xs text-foreground-500">{property.type}</span>
                      {!property.active && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold tracking-wider text-foreground-600 bg-background-200">
                          INATIVO
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 truncate">{property.title}</h3>
                    <p className="text-xs text-foreground-500 truncate">{property.location}</p>
                  </div>

                  <p className="text-sm font-bold text-primary-600 whitespace-nowrap hidden sm:block">
                    {property.price}
                  </p>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => handleToggleActive(property)}
                      disabled={togglingId === property.id}
                      aria-label={property.active ? 'Inativar imóvel' : 'Ativar imóvel'}
                      title={property.active ? 'Inativar imóvel' : 'Ativar imóvel'}
                      className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-200 hover:text-secondary-900 transition-colors disabled:opacity-50"
                    >
                      <i className={
                        togglingId === property.id
                          ? 'ri-loader-4-line animate-spin'
                          : property.active
                            ? 'ri-eye-off-line'
                            : 'ri-eye-line'
                      }></i>
                    </button>
                    <button
                      onClick={() => navigate(`/admin/imoveis/${property.id}/editar`)}
                      aria-label="Editar imóvel"
                      className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-200 hover:text-primary-600 transition-colors"
                    >
                      <i className="ri-pencil-line"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(property)}
                      disabled={deletingId === property.id}
                      aria-label="Excluir imóvel"
                      className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
                    >
                      <i className={deletingId === property.id ? 'ri-loader-4-line animate-spin' : 'ri-delete-bin-line'}></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
